import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DataTable,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  Stack,
  StatusChip,
  statusTone,
  Typography,
} from '@munifocus/ui';
import type {
  ClerkDashboard as ClerkDashboardData,
  ClerkDashboardAppointment,
  ClerkDashboardFile,
  CouncilAuthorizationView,
  Form4NameApprovalView,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { COUNCIL_STATUS_KIND, FORM4_STATUS_KIND } from '../components/clerk/ClerkApprovalsPanel';
import {
  CouncilDecisionDialog,
  Form4DecisionDialog,
} from '../components/clerk/ClerkDecisionDialogs';
import { useClerkDashboard } from '../lib/clerk-api';
import { formatDate, formatDateTime } from '../lib/relative-time';
import { useScope } from '../providers/ScopeProvider';

/** One number at the top of the page, linking to its section. */
function Tile({ label, value, href }: { label: string; value: number; href: string }) {
  return (
    <HairlineCard sx={{ p: 2, flex: '1 1 160px', minWidth: 0 }}>
      <Link href={href} underline="none" color="inherit">
        <Typography variant="h4" component="p" sx={{ fontWeight: 800 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Link>
    </HairlineCard>
  );
}

function Section({
  id,
  title,
  subtitle,
  aside,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box component="section" id={id} aria-labelledby={`${id}-title`} sx={{ scrollMarginTop: 80 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' }, gap: 1, mb: 1 }}
      >
        <Box>
          <Typography variant="h6" component="h2" id={`${id}-title`}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {aside}
      </Stack>
      {children}
    </Box>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
      {text}
    </Typography>
  );
}

/** A list of approval rows inside one card: the file, what happened, the status, the action. */
function ApprovalList<T extends { id: string; fileId: string; fileReference: string }>({
  rows,
  empty,
  describe,
  status,
  rowControl,
}: {
  rows: T[];
  empty: string;
  describe: (row: T) => (string | null)[];
  status: (row: T) => React.ReactNode;
  rowControl?: (row: T) => React.ReactNode;
}) {
  if (rows.length === 0) return <Empty text={empty} />;
  return (
    <Box>
      {rows.map((row, i) => (
        <Stack
          key={row.id}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
            justifyContent: 'space-between',
            alignItems: { sm: 'center' },
            borderTop: i === 0 ? 'none' : 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Link
              component={RouterLink}
              to={`/files/${row.fileId}`}
              underline="hover"
              sx={{ fontWeight: 700 }}
            >
              {row.fileReference}
            </Link>
            {describe(row)
              .filter(Boolean)
              .map((line, j) => (
                <Typography
                  key={j}
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {line}
                </Typography>
              ))}
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
            {status(row)}
            {rowControl?.(row)}
          </Stack>
        </Stack>
      ))}
    </Box>
  );
}

function AppointmentsSection({ data }: { data: ClerkDashboardData }) {
  const { t, i18n } = useTranslation();
  return (
    <Section
      id="appointments"
      title={t('gov.clerk.dashboard.appointments.title')}
      subtitle={t('gov.clerk.dashboard.appointments.subtitle')}
      aside={
        <Button component={RouterLink} to="/administration/officers" size="small">
          {t('gov.clerk.dashboard.appointments.manage')}
        </Button>
      }
    >
      {data.appointments.waitingCount > 0 && (
        // Approving an appointment happens on the appointments screen; this is
        // the dashboard's hook into it.
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('gov.clerk.dashboard.appointments.waiting', {
            count: data.appointments.waitingCount,
          })}{' '}
          <Link component={RouterLink} to="/administration/officers">
            {t('gov.clerk.dashboard.appointments.review')}
          </Link>
        </Alert>
      )}
      <DataTable<ClerkDashboardAppointment>
        rows={data.appointments.items}
        getRowKey={(row) => row.id}
        emptyLabel={t('gov.clerk.dashboard.appointments.empty')}
        // On a phone each appointment is a card, so the officer, company and
        // date are never behind a sideways scroll.
        stackOnPhone
        initialSortKey="appointedOn"
        initialSortDir="desc"
        pageSize={10}
        columns={[
          {
            key: 'officer',
            label: t('gov.clerk.dashboard.appointments.officer'),
            primary: true,
            sortValue: (row) => row.officerName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.officerName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.officerEmail}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'company',
            label: t('gov.clerk.dashboard.appointments.company'),
            sortValue: (row) => row.companyName ?? '',
            cell: (row) => row.companyName ?? t('gov.admin.officers.directEmployee'),
          },
          {
            key: 'appointedOn',
            label: t('gov.clerk.dashboard.appointments.appointedOn'),
            nowrap: true,
            sortValue: (row) => row.appointedOn ?? '',
            cell: (row) => formatDate(row.appointedOn, i18n.language),
          },
          {
            key: 'status',
            label: t('gov.clerk.dashboard.appointments.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <StatusChip
                status={statusTone.appointment[row.status]}
                label={t(`gov.admin.officers.statuses.${row.status}`)}
              />
            ),
          },
          {
            key: 'badge',
            label: t('gov.clerk.dashboard.appointments.badge'),
            mono: true,
            nowrap: true,
            sortValue: (row) => row.badgeNumber,
            cell: (row) => row.badgeNumber || '—',
          },
        ]}
      />
    </Section>
  );
}

function Form4Section({ data }: { data: ClerkDashboardData }) {
  const { t, i18n } = useTranslation();
  const [deciding, setDeciding] = useState<Form4NameApprovalView | null>(null);
  const status = (row: Form4NameApprovalView) => (
    <StatusChip
      status={FORM4_STATUS_KIND[row.status]}
      label={t(`gov.clerk.form4.status.${row.status}`)}
    />
  );
  return (
    <Section
      id="form4"
      title={t('gov.clerk.dashboard.form4.title')}
      subtitle={t('gov.clerk.dashboard.form4.subtitle')}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {t('gov.clerk.dashboard.waiting', { count: data.form4.waiting.length })}
          </Typography>
          <HairlineCard sx={{ p: 0 }}>
            <ApprovalList
              rows={data.form4.waiting}
              empty={t('gov.clerk.dashboard.form4.emptyWaiting')}
              describe={(row) => [
                row.fileAddress,
                t('gov.clerk.requestedBy', {
                  name: row.requestedByName,
                  at: formatDateTime(row.requestedAt, i18n.language),
                }),
                row.requestNote,
              ]}
              status={status}
              rowControl={(row) => (
                <Button size="small" variant="contained" onClick={() => setDeciding(row)}>
                  {t('gov.clerk.answer')}
                </Button>
              )}
            />
          </HairlineCard>
        </Box>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {t('gov.clerk.dashboard.form4.approved')}
          </Typography>
          <HairlineCard sx={{ p: 0 }}>
            <ApprovalList
              rows={[...data.form4.approved, ...data.form4.declined]}
              empty={t('gov.clerk.dashboard.form4.emptyApproved')}
              describe={(row) => [
                row.fileAddress,
                t('gov.clerk.decidedBy', {
                  name: row.decidedByName ?? '',
                  at: formatDateTime(row.decidedAt ?? '', i18n.language),
                }),
                row.decisionComment,
              ]}
              status={status}
            />
          </HairlineCard>
        </Box>
      </Stack>
      {deciding && <Form4DecisionDialog approval={deciding} onClose={() => setDeciding(null)} />}
    </Section>
  );
}

function CouncilSection({ data }: { data: ClerkDashboardData }) {
  const { t, i18n } = useTranslation();
  const [deciding, setDeciding] = useState<CouncilAuthorizationView | null>(null);
  const status = (row: CouncilAuthorizationView) => (
    <StatusChip
      status={COUNCIL_STATUS_KIND[row.status]}
      label={t(`gov.clerk.council.status.${row.status}`)}
    />
  );
  const kind = (row: CouncilAuthorizationView) => t(`gov.clerk.council.kinds.${row.kind}`);
  return (
    <Section
      id="council"
      title={t('gov.clerk.dashboard.council.title')}
      subtitle={t('gov.clerk.dashboard.council.subtitle')}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {t('gov.clerk.dashboard.waiting', { count: data.council.waiting.length })}
          </Typography>
          <HairlineCard sx={{ p: 0 }}>
            <ApprovalList
              rows={data.council.waiting}
              empty={t('gov.clerk.dashboard.council.emptyWaiting')}
              describe={(row) => [
                `${kind(row)} · ${row.fileAddress}`,
                t('gov.clerk.requestedBy', {
                  name: row.requestedByName,
                  at: formatDateTime(row.requestedAt, i18n.language),
                }),
                row.requestNote,
              ]}
              status={status}
              rowControl={(row) => (
                <Button size="small" variant="contained" onClick={() => setDeciding(row)}>
                  {t('gov.clerk.council.record')}
                </Button>
              )}
            />
          </HairlineCard>
        </Box>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {t('gov.clerk.dashboard.council.authorized')}
          </Typography>
          <HairlineCard sx={{ p: 0 }}>
            <ApprovalList
              rows={[...data.council.authorized, ...data.council.declined]}
              empty={t('gov.clerk.dashboard.council.emptyAuthorized')}
              describe={(row) => [
                `${kind(row)} · ${row.fileAddress}`,
                t('gov.clerk.council.resolutionLine', {
                  ref: row.resolutionRef ?? '—',
                  date: formatDate(row.councilMeetingDate, i18n.language),
                }),
                t('gov.clerk.decidedBy', {
                  name: row.decidedByName ?? '',
                  at: formatDateTime(row.decidedAt ?? '', i18n.language),
                }),
                row.notes,
              ]}
              status={status}
            />
          </HairlineCard>
        </Box>
      </Stack>
      {deciding && (
        <CouncilDecisionDialog authorization={deciding} onClose={() => setDeciding(null)} />
      )}
    </Section>
  );
}

function stageLabel(file: ClerkDashboardFile, t: (key: string) => string): string {
  const stage = t(`gov.stages.${file.stage}`);
  return file.escalationStep
    ? `${stage} · ${t(`gov.escalation.steps.${file.escalationStep}`)}`
    : stage;
}

function ActiveFilesSection({ data }: { data: ClerkDashboardData }) {
  const { t, i18n } = useTranslation();
  const stages = Object.entries(data.activeFiles.byStage);
  return (
    <Section
      id="files"
      title={t('gov.clerk.dashboard.files.title', { count: data.activeFiles.count })}
      subtitle={t('gov.clerk.dashboard.files.subtitle')}
    >
      {stages.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {stages.map(([stage, count]) => (
            <StatusChip
              key={stage}
              status="neutral"
              label={t('gov.clerk.dashboard.files.stageCount', {
                stage: t(`gov.stages.${stage}`),
                count,
              })}
            />
          ))}
        </Stack>
      )}
      {data.activeFiles.truncated && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('gov.clerk.dashboard.files.truncated', {
            shown: data.activeFiles.items.length,
            count: data.activeFiles.count,
          })}
        </Alert>
      )}
      <DataTable<ClerkDashboardFile>
        rows={data.activeFiles.items}
        getRowKey={(row) => row.id}
        rowHref={(row) => `/files/${row.id}`}
        linkComponent={RouterLink}
        emptyLabel={t('gov.clerk.dashboard.files.empty')}
        stackOnPhone
        initialSortKey="updatedAt"
        initialSortDir="desc"
        pageSize={25}
        search={{
          placeholder: t('gov.clerk.dashboard.files.search'),
          of: (row) => `${row.reference} ${row.address} ${row.leadOfficerName ?? ''}`,
        }}
        columns={[
          {
            key: 'reference',
            label: t('gov.clerk.dashboard.files.reference'),
            primary: true,
            mono: true,
            nowrap: true,
            sortValue: (row) => row.reference,
            cell: (row) => row.reference,
          },
          {
            key: 'address',
            label: t('gov.clerk.dashboard.files.address'),
            sortValue: (row) => row.address,
            cell: (row) => row.address || '—',
          },
          {
            key: 'stage',
            label: t('gov.clerk.dashboard.files.stage'),
            sortValue: (row) => `${row.stage}:${row.escalationStep ?? ''}`,
            cell: (row) => stageLabel(row, t),
          },
          {
            key: 'officer',
            label: t('gov.clerk.dashboard.files.officer'),
            sortValue: (row) => row.leadOfficerName ?? '',
            cell: (row) => row.leadOfficerName ?? '—',
          },
          {
            key: 'updatedAt',
            label: t('gov.clerk.dashboard.files.updated'),
            nowrap: true,
            sortValue: (row) => row.updatedAt,
            cell: (row) => formatDate(row.updatedAt, i18n.language),
          },
        ]}
      />
    </Section>
  );
}

/**
 * The Clerk dashboard: appointments, Form 4 name approvals, council
 * authorizations and every active file with its stage, for the municipality in
 * the active scope. Visible to the clerk seat — the Clerk, and the Deputy and
 * Assistant Clerks who hold it under their own titles — including at a
 * municipality that does not subscribe, where the files read but do not edit.
 */
export function ClerkDashboard() {
  const { t } = useTranslation();
  const { active } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : null;
  const dashboard = useClerkDashboard(localGovernmentId);
  const data = dashboard.data;

  return (
    <AppShell>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', gap: 1, mb: 2, alignItems: { sm: 'flex-end' } }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Icons.BadgeCheck size={iconSize.lg} aria-hidden />
            <Typography variant="h5" component="h1">
              {t('gov.clerk.dashboard.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {data
              ? t('gov.clerk.dashboard.subtitleFor', { municipality: data.localGovernmentName })
              : t('gov.clerk.dashboard.subtitle')}
          </Typography>
        </Box>
        {data?.canAddClerks && (
          <Button
            component={RouterLink}
            to="/administration/members"
            size="small"
            startIcon={<Icons.UserPlus size={iconSize.sm} />}
          >
            {t('gov.clerk.dashboard.addClerk')}
          </Button>
        )}
      </Stack>

      {!localGovernmentId && (
        <Alert severity="info">{t('gov.clerk.dashboard.pickMunicipality')}</Alert>
      )}
      {dashboard.isLoading && <CircularProgress size={24} />}
      {dashboard.isError && <Alert severity="error">{t('gov.clerk.dashboard.loadFailed')}</Alert>}

      {data && (
        <Stack spacing={4}>
          {data.readOnly && <Alert severity="info">{t('gov.clerk.dashboard.readOnly')}</Alert>}
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Tile
              label={t('gov.clerk.dashboard.tiles.appointments')}
              value={data.appointments.items.filter((a) => a.status === 'active').length}
              href="#appointments"
            />
            <Tile
              label={t('gov.clerk.dashboard.tiles.form4')}
              value={data.form4.waiting.length}
              href="#form4"
            />
            <Tile
              label={t('gov.clerk.dashboard.tiles.council')}
              value={data.council.waiting.length}
              href="#council"
            />
            <Tile
              label={t('gov.clerk.dashboard.tiles.files')}
              value={data.activeFiles.count}
              href="#files"
            />
          </Stack>
          <AppointmentsSection data={data} />
          <Form4Section data={data} />
          <CouncilSection data={data} />
          <ActiveFilesSection data={data} />
        </Stack>
      )}
    </AppShell>
  );
}
